import { css } from '@compiled/react';

import { usePrefersReducedMotion } from './jsm-playground-motion';

const FPS = 30;
const TOTAL_FRAMES = 108;
const CYCLE_S = TOTAL_FRAMES / FPS;

const LOOP_CLOSE_FRAME = 107;

const MORPH_SPLINE = '0.25 0 0.5 1';

const CIRCLE =
	'M50 0L56.5 0.4L62.9 1.7L69.1 3.8L75 6.7L80.4 10.3L85.4 14.6L89.7 19.6L93.3 25L96.2 30.9L98.3 37.1L99.6 43.5L100 50L99.6 56.5L98.3 62.9L96.2 69.1L93.3 75L89.7 80.4L85.4 85.4L80.4 89.7L75 93.3L69.1 96.2L62.9 98.3L56.5 99.6L50 100L43.5 99.6L37.1 98.3L30.9 96.2L25 93.3L19.6 89.7L14.6 85.4L10.3 80.4L6.7 75L3.8 69.1L1.7 62.9L0.4 56.5L0 50L0.4 43.5L1.7 37.1L3.8 30.9L6.7 25L10.3 19.6L14.6 14.6L19.6 10.3L25 6.7L30.9 3.8L37.1 1.7L43.5 0.4Z';
const SQUARE =
	'M0 0L8.3 0L16.7 0L25 0L33.3 0L41.7 0L50 0L58.3 0L66.7 0L75 0L83.3 0L91.7 0L100 0L100 8.3L100 16.7L100 25L100 33.3L100 41.7L100 50L100 58.3L100 66.7L100 75L100 83.3L100 91.7L100 100L91.7 100L83.3 100L75 100L66.7 100L58.3 100L50 100L41.7 100L33.3 100L25 100L16.7 100L8.3 100L0 100L0 91.7L0 83.3L0 75L0 66.7L0 58.3L0 50L0 41.7L0 33.3L0 25L0 16.7L0 8.3Z';
const HEXAGON =
	'M50 0L55.4 3.1L60.8 6.3L66.2 9.4L71.7 12.5L77.1 15.6L82.5 18.8L87.9 21.9L93.3 25L93.3 31.3L93.3 37.5L93.3 43.8L93.3 50L93.3 56.3L93.3 62.5L93.3 68.8L93.3 75L87.9 78.1L82.5 81.3L77.1 84.4L71.7 87.5L66.2 90.6L60.8 93.8L55.4 96.9L50 100L44.6 96.9L39.2 93.8L33.8 90.6L28.3 87.5L22.9 84.4L17.5 81.3L12.1 78.1L6.7 75L6.7 68.8L6.7 62.5L6.7 56.3L6.7 50L6.7 43.8L6.7 37.5L6.7 31.2L6.7 25L12.1 21.9L17.5 18.7L22.9 15.6L28.3 12.5L33.8 9.4L39.2 6.3L44.6 3.1Z';
const TRIANGLE =
	'M50 0L53.1 6.3L56.3 12.5L59.4 18.8L62.5 25L65.6 31.3L68.8 37.5L71.9 43.8L75 50L78.1 56.3L81.3 62.5L84.4 68.8L87.5 75L90.6 81.3L93.8 87.5L96.9 93.8L100 100L93.8 100L87.5 100L81.3 100L75 100L68.8 100L62.5 100L56.3 100L50 100L43.8 100L37.5 100L31.3 100L25 100L18.8 100L12.5 100L6.3 100L0 100L3.1 93.8L6.3 87.5L9.4 81.3L12.5 75L15.6 68.8L18.8 62.5L21.9 56.3L25 50L28.1 43.8L31.3 37.5L34.4 31.3L37.5 25L40.6 18.8L43.8 12.5L46.9 6.3Z';

const AMBER = '#F5A623';
const GREEN = '#82B536';
const BLUE = '#1868DB';
const PURPLE = '#BF63F3';

type Track = { frame: number; value: string }[];

const OUTLINE: Track = [
	{ frame: 0, value: CIRCLE },
	{ frame: 27, value: SQUARE },
	{ frame: 54, value: HEXAGON },
	{ frame: 80, value: TRIANGLE },
	{ frame: LOOP_CLOSE_FRAME, value: CIRCLE },
	{ frame: TOTAL_FRAMES, value: CIRCLE },
];

const ROTATION: Track = [
	{ frame: 0, value: '0 50 50' },
	{ frame: 27, value: '180 50 50' },
	{ frame: 54, value: '240 50 50' },
	{ frame: 80, value: '360 50 50' },
	{ frame: LOOP_CLOSE_FRAME, value: '450 50 50' },
	{ frame: TOTAL_FRAMES, value: '450 50 50' },
];

const FILL: Track = [
	{ frame: 0, value: AMBER },
	{ frame: 5, value: AMBER },
	{ frame: 11, value: GREEN },
	{ frame: 32, value: GREEN },
	{ frame: 37, value: BLUE },
	{ frame: 59, value: BLUE },
	{ frame: 64, value: PURPLE },
	{ frame: 86, value: PURPLE },
	{ frame: 91, value: AMBER },
	{ frame: TOTAL_FRAMES, value: AMBER },
];

const values = (track: Track) => track.map((step) => step.value).join(';');
const keyTimes = (track: Track) =>
	track.map((step) => (step.frame / TOTAL_FRAMES).toFixed(6)).join(';');
const keySplines = (track: Track) =>
	Array(track.length - 1)
		.fill(MORPH_SPLINE)
		.join(';');

const SHAPE_SIZE = 16;
const SLOT_SIZE = SHAPE_SIZE + 8;

export const JsmPlaygroundRovoMorphingShape = () => {
	const prefersReducedMotion = usePrefersReducedMotion();
	const duration = `${CYCLE_S}s`;

	return (
		<span css={slotStyles}>
			<svg
				css={shapeStyles}
				width={SHAPE_SIZE}
				height={SHAPE_SIZE}
				viewBox="0 0 100 100"
				aria-hidden="true"
				focusable="false"
			>
				<path d={CIRCLE} fill={AMBER}>
					{!prefersReducedMotion && (
						<>
							<animate
								attributeName="d"
								dur={duration}
								repeatCount="indefinite"
								calcMode="spline"
								values={values(OUTLINE)}
								keyTimes={keyTimes(OUTLINE)}
								keySplines={keySplines(OUTLINE)}
							/>
							<animateTransform
								attributeName="transform"
								type="rotate"
								dur={duration}
								repeatCount="indefinite"
								calcMode="spline"
								values={values(ROTATION)}
								keyTimes={keyTimes(ROTATION)}
								keySplines={keySplines(ROTATION)}
							/>
							<animate
								attributeName="fill"
								dur={duration}
								repeatCount="indefinite"
								values={values(FILL)}
								keyTimes={keyTimes(FILL)}
							/>
						</>
					)}
				</path>
			</svg>
		</span>
	);
};

const slotStyles = css({
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	flexShrink: 0,
	width: `${SLOT_SIZE}px`,
	height: `${SLOT_SIZE}px`,
});

const shapeStyles = css({
	display: 'block',
	overflow: 'hidden',
});
